import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import cs from 'classnames';

import { ia } from 'lib/helpers/utility';

import { useTableContext } from '../../../lib/context/TableContext/TableContext';
import { RowSelectionActions } from '../AgTablePage/components/RowSelectionActions';
import Button from '../Button/Button';
import { getActiveFilters } from '../Filters/lib/helpers';
import Icon from '../Icon/Icon';

import { SkeletonCellRenderer } from './SkeletonCellRenderer';
import CustomLoadingOverlay from './customLoadingOverlay';
import CustomNoRowsOverlay from './customNoRowsOverlay';

import './Table.scss';

LicenseManager.setLicenseKey(process.env.REACT_AG_GRID_KEY);

const EmptyData = () => {
  const { filters, updateFilters } = useTableContext();
  const [_, setSearchParams] = useSearchParams();

  const isFilterApplied = ia(getActiveFilters(filters));

  if (isFilterApplied) {
    return (
      <div className="flex flex-col items-center gap-1">
        <Icon icon="illustration-empty-table" />
        <h1 className="text-base font-500 text-primary-900">No results match your filters</h1>
        <h4 className="text-sm text-neutral-600">Adjust the filters to see more results.</h4>
        <Button
          text="Clear Filters"
          icon="close-square-mono"
          variant="link"
          size="xs"
          onClick={() => {
            setSearchParams({});
            updateFilters(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <Icon icon="illustration-empty-table" />
      <h1 className="text-base font-500 text-primary-900">No Data Available</h1>
    </div>
  );
};

const CustomDetailCellRenderer = (props) => {
  const { api, node, data, ExpandedComponent } = props;
  const containerRef = useRef(null);
  const resizeObserverRef = useRef(null);

  useEffect(() => {
    resizeObserverRef.current = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        for (const entry of entries) {
          const newHeight = entry.contentRect.height;
          if (newHeight > 0 && node.rowHeight !== newHeight) {
            node.setRowHeight(newHeight);
            api.onRowHeightChanged();
          }
        }
      });
    });

    if (containerRef.current) {
      resizeObserverRef.current.observe(containerRef.current);
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [api, node]);

  return (
    <div ref={containerRef}>
      <ExpandedComponent {...props} src={data?.details} />
    </div>
  );
};

const AGTable = ({
  data,
  customClassName,
  ExpandedComponent,
  loading,
  skeletonSize = 25,
  actions,
  count,
  onGridReady: proppedOnGridReady = () => {},
  ...props
}) => {
  const {
    onGridReady,
    onColumnMoved,
    colDefs,
    onSortChanged,
    onSelectionChanged,
    pagination,
    gridRef,
    tableLoading,
    limit
  } = useTableContext();
  const isLoading = loading || tableLoading;

  const defaultColDef = useMemo(
    () => ({
      valueFormatter: ({ value }) => (value == null || value == '' || value == ' ' ? '-' : value)
    }),
    []
  );

  const components = useMemo(
    () => ({ agLoadingOverlay: CustomLoadingOverlay, agNoRowsOverlay: CustomNoRowsOverlay }),
    []
  );

  const onCellClicked = useCallback((event) => {
    event.api.clearFocusedCell();
  }, []);

  const detailGridOptions = useMemo(
    () => ({
      onFirstDataRendered: (params) => {
        params.api.forEachNode((node) => {
          if (node.expanded) {
            node.setExpanded(false);
            params.api.clearFocusedCell();
          }
        });
      }
    }),
    []
  );

  const handleSortChange = useCallback(
    (event) => {
      if (Number(count) <= Number(limit)) return;
      onSortChanged(event);
    },
    [onSortChanged, count, limit]
  );

  const DetailCellRenderer = useCallback(
    (props) => <CustomDetailCellRenderer {...props} ExpandedComponent={ExpandedComponent} />,
    []
  );

  const onGridReadyHandler = useCallback((props) => {
    onGridReady(props);
    proppedOnGridReady(props);
  }, []);

  const detailCellRendererParams = useMemo(
    () => ({ detailGridOptions, suppressRowClickSelection: true }),
    [detailGridOptions]
  );

  const updatedColDefs = useMemo(() => {
    return colDefs.map((col) => ({
      ...col,
      valueFormatter: (params) => {
        let value;

        if (typeof col?.valueFormatter === 'function') {
          value = col?.valueFormatter(params);
        } else {
          value = params?.value;
        }

        return value == null || value === '' || value === ' ' ? '-' : value;
      },
      cellRendererSelector: isLoading ? () => ({ component: SkeletonCellRenderer }) : undefined
    }));
  }, [colDefs, isLoading]);

  const updatedRowData = useMemo(() => {
    if (!isLoading) return data;
    return Array.from({ length: skeletonSize }, (_, index) => ({ index }));
  }, [data, isLoading, skeletonSize]);

  return (
    <div
      className={cs(
        'ag-theme-quartz relative h-full overflow-y-hidden',
        pagination && 'ag-pagination',
        customClassName
      )}>
      <AgGridReact
        ref={gridRef}
        onColumnRowGroupChanged={onColumnMoved}
        onColumnMoved={onColumnMoved}
        onColumnPinned={onColumnMoved}
        onColumnVisible={onColumnMoved}
        onGridReady={onGridReadyHandler}
        onSortChanged={handleSortChange}
        rowData={updatedRowData}
        columnDefs={updatedColDefs}
        // enableRangeSelection={true}
        defaultColDef={defaultColDef}
        components={components}
        onCellClicked={onCellClicked}
        onSelectionChanged={onSelectionChanged}
        onRowSelected={onSelectionChanged}
        detailCellRenderer={DetailCellRenderer}
        detailCellRendererParams={detailCellRendererParams}
        noRowsOverlayComponent={EmptyData}
        suppressLoadingOverlay
        {...props}
      />
      <RowSelectionActions actions={actions} />
    </div>
  );
};

export default memo(AGTable);
